// views/Dashboard/PainelExecutivo/sections/ceo/KpisDepartamentos.tsx
//
// "KPI com 3 indicadores para cada departamento: os gestores criam e eu valido."
//
// Ninguém começa de uma tela vazia: cada departamento já abre com 3 KPIs que o
// sistema calcula sozinho (catálogo em kpisAutomaticos.ts) — com o número do mês,
// a comparação com o anterior e uma meta sugerida. O Igor valida com um clique,
// troca por uma das alternativas ou recusa; o gestor ajusta a meta e propõe, ou
// cria um KPI próprio (valor digitado à mão).
//
// Quem valida e o limite de 3 são regras do banco (trigger `kpis_departamento_regras`).
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, Check, CheckCheck, ChevronDown, Loader2, Plus, Repeat, RotateCcw, Trash2, X } from 'lucide-react';
import { useAuth } from '../../../../../src/contexts/AuthContext';
import { useToast } from '../../../../../src/contexts/ToastContext';
import { useKpisDepartamento } from './useCeoDados';
import type { NovoKpi } from './useCeoDados';
import {
  DEPARTAMENTOS, LIMITE_KPIS_POR_DEPARTAMENTO, UNIDADES_KPI, VALIDAR_KPI_PERMISSION,
  atingimentoKpi, formatarValorKpi, kpiAtivo, rotuloDepartamento,
} from '../../../../../src/lib/dashboard/kpisDepartamento';
import type { KpiDepartamento, UnidadeKpi } from '../../../../../src/lib/dashboard/kpisDepartamento';
import { kpiAutomatico, kpisAutomaticosDe, melhoraDoKpi } from '../../../../../src/lib/dashboard/kpisAutomaticos';
import type { ContextoKpis, DefinicaoKpiAuto, ValorKpi } from '../../../../../src/lib/dashboard/kpisAutomaticos';
import { faixaDoAtingimento } from '../../../../../src/lib/dashboard/painelCeo';
import { chaveDoKpi, mesDaMeta, metaDoMes, rotuloDoMes, type MetaMensal } from '../../../../../src/lib/dashboard/metasMensais';
import { useMetasMensais } from '../metas/useMetasMensais';
import { BarraMeta, Bloco, BotaoPrimario, BotaoSecundario, COR_FAIXA, Destaque, ErroBloco, LinhasCarregando, pct } from './ui';

const lerNumero = (texto: string): number | null => {
  const limpo = texto.trim().replace(/\./g, '').replace(',', '.');
  if (!limpo) return null;
  const v = Number(limpo);
  return Number.isFinite(v) ? v : null;
};
const escreverNumero = (v: number | null | undefined) =>
  v == null ? '' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 2, useGrouping: false });
const dataCurta = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '');

const CAMPO =
  'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition-[border-color,box-shadow] placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white';

/** Um lugar no departamento: um KPI do banco (proposto/validado) ou uma sugestão do catálogo. */
type Slot =
  | { tipo: 'db'; kpi: KpiDepartamento; def: DefinicaoKpiAuto | undefined }
  | { tipo: 'sugestao'; def: DefinicaoKpiAuto };

interface Props { ctx: ContextoKpis }

const KpisDepartamentos: React.FC<Props> = ({ ctx }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const kpis = useKpisDepartamento();
  // Meta do mês definida na aba Metas: vale no lugar da meta do KPI naquele mês.
  const { metas: metasMensais } = useMetasMensais();
  const podeValidar = !!profile?.permissions?.includes(VALIDAR_KPI_PERMISSION);
  const [formPara, setFormPara] = useState<string | null>(null);
  const [validandoTudo, setValidandoTudo] = useState(false);

  const porDepartamento = useMemo(() => DEPARTAMENTOS.map((d) => {
    const doDep = kpis.kpis.filter((k) => k.departamento === d.id);
    const ativos = doDep.filter(kpiAtivo);
    const usados = new Set(doDep.map((k) => k.calculo_automatico).filter(Boolean));
    const livres = kpisAutomaticosDe(d.id).filter((k) => !usados.has(k.id));
    const vagas = Math.max(0, LIMITE_KPIS_POR_DEPARTAMENTO - ativos.length);
    const slots: Slot[] = [
      ...ativos.map((k) => ({ tipo: 'db' as const, kpi: k, def: kpiAutomatico(k.calculo_automatico) })),
      ...livres.slice(0, vagas).map((def) => ({ tipo: 'sugestao' as const, def })),
    ];
    return { dep: d, slots, alternativas: livres.slice(vagas), ativos: ativos.length, recusados: doDep.filter((k) => !kpiAtivo(k)) };
  }), [kpis.kpis]);

  const validados = kpis.kpis.filter((k) => k.status === 'validado').length;
  const aguardando = kpis.kpis.filter((k) => k.status === 'proposto').length;
  const sugestoesAbertas = porDepartamento.flatMap((d) => d.slots.filter((s) => s.tipo === 'sugestao'));
  const total = DEPARTAMENTOS.length * LIMITE_KPIS_POR_DEPARTAMENTO;

  const executar = async (acao: () => Promise<void>, sucesso: string) => {
    try {
      await acao();
      addToast(sucesso, 'success');
      return true;
    } catch (e: any) {
      addToast(e?.message || 'Não foi possível salvar.', 'error');
      return false;
    }
  };

  /** A sugestão vira KPI de verdade: validado (se quem clica valida) ou proposto. */
  const adotar = (def: DefinicaoKpiAuto, meta: number, status: 'validado' | 'proposto' | 'recusado' = podeValidar ? 'validado' : 'proposto'): NovoKpi => ({
    departamento: def.departamento, nome: def.nome, como_medir: def.como_medir, unidade: def.unidade, sentido: def.sentido,
    meta, valor_atual: null, calculo_automatico: def.id, status,
  });

  const metaDe = (def: DefinicaoKpiAuto) => {
    const v = def.calcular(ctx);
    return v ? def.metaSugerida(v, ctx) : 0;
  };

  const validarTodas = async () => {
    setValidandoTudo(true);
    await executar(
      () => kpis.proporVarios(sugestoesAbertas.map((s) => adotar(s.def, metaDe(s.def), 'validado'))),
      `${sugestoesAbertas.length} KPIs validados com a meta sugerida.`,
    );
    setValidandoTudo(false);
  };

  return (
    <Bloco
      titulo={kpis.loading ? 'KPIs dos departamentos' : `KPIs: ${validados} de ${total} validados`}
      subtitulo="Os números já saem do sistema. O gestor ajusta a meta ou troca o indicador; o Igor valida."
      acoes={
        <>
          {podeValidar && sugestoesAbertas.length > 0 && (
            <BotaoPrimario onClick={validarTodas} disabled={validandoTudo}>
              {validandoTudo ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />}
              Validar as {sugestoesAbertas.length} sugestões
            </BotaoPrimario>
          )}
          {formPara === null && (
            <BotaoSecundario onClick={() => setFormPara(porDepartamento.find((d) => d.ativos < LIMITE_KPIS_POR_DEPARTAMENTO)?.dep.id ?? DEPARTAMENTOS[0].id)}>
              <Plus size={14} /> KPI próprio
            </BotaoSecundario>
          )}
        </>
      }
      destaque={
        kpis.loading || kpis.erro ? null : (
          <Destaque
            valor={`${validados}/${total}`}
            legenda={aguardando ? `${aguardando} aguardando ${podeValidar ? 'você' : 'o Igor'}` : sugestoesAbertas.length ? `${sugestoesAbertas.length} sugestões prontas` : 'todos definidos'}
          />
        )
      }
    >
      {kpis.erro ? (
        <ErroBloco mensagem={kpis.erro} />
      ) : kpis.loading ? (
        <LinhasCarregando linhas={3} rotulo="Carregando os KPIs…" />
      ) : (
        <>
          {formPara !== null && (
            <FormularioKpi
              departamentoInicial={formPara}
              cheio={(dep) => (porDepartamento.find((d) => d.dep.id === dep)?.ativos ?? 0) >= LIMITE_KPIS_POR_DEPARTAMENTO}
              onCancelar={() => setFormPara(null)}
              onEnviar={async (novo) => {
                const ok = await executar(() => kpis.propor(novo), podeValidar ? 'KPI criado.' : 'KPI enviado para validação.');
                if (ok) setFormPara(null);
              }}
            />
          )}

          {/* Grade de linhas finas: cada departamento é uma célula, sem caixa dentro de caixa. */}
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-b-2xl border-t border-gray-100 bg-gray-100 dark:border-gray-700 dark:bg-gray-700 md:grid-cols-2 xl:grid-cols-3">
            {porDepartamento.map(({ dep, slots, alternativas, recusados }) => (
              <div key={dep.id} className="bg-white p-5 dark:bg-gray-800">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">{dep.rotulo}</h3>
                  <span className="text-[11px] tabular-nums text-gray-400">
                    {slots.filter((s) => s.tipo === 'db' && s.kpi.status === 'validado').length} de {LIMITE_KPIS_POR_DEPARTAMENTO} validados
                  </span>
                </div>
                <ul className="-mx-2 space-y-0.5">
                  {slots.map((s) => (
                    <ItemKpi
                      key={s.tipo === 'db' ? `db-${s.kpi.id}` : `sug-${s.def.id}`}
                      slot={s}
                      ctx={ctx}
                      metasMensais={metasMensais}
                      nomes={kpis.nomes}
                      podeValidar={podeValidar}
                      meuId={profile?.id ?? null}
                      alternativas={alternativas}
                      onAdotar={(def, meta) => executar(() => kpis.propor(adotar(def, meta)), podeValidar ? 'KPI validado.' : 'KPI proposto para o Igor validar.')}
                      onTrocar={(atual, nova) => executar(async () => {
                        // A sugestão trocada fica registrada como recusada, para não voltar.
                        if (podeValidar) await kpis.proporVarios([adotar(nova, metaDe(nova)), adotar(atual, metaDe(atual), 'recusado')]);
                        else await kpis.propor(adotar(nova, metaDe(nova)));
                      }, `Trocado por "${nova.nome}".`)}
                      onRecusarSugestao={(def) => executar(() => kpis.propor(adotar(def, metaDe(def), 'recusado')), `"${def.nome}" saiu da lista.`)}
                      onAtualizar={(id, campos, msg) => executar(() => kpis.atualizar(id, campos), msg)}
                      onApagar={(id) => executar(() => kpis.apagar(id), 'KPI apagado.')}
                    />
                  ))}
                </ul>
                {recusados.length > 0 && (
                  <p className="mt-3 text-[11px] text-gray-400">
                    Recusados: {recusados.map((k) => k.nome).join(', ')}
                    {podeValidar && (
                      <button type="button" className="ml-1 font-semibold text-gray-500 underline-offset-2 hover:underline"
                        onClick={() => recusados.forEach((k) => executar(() => kpis.apagar(k.id), `"${k.nome}" voltou para as sugestões.`))}>
                        devolver às sugestões
                      </button>
                    )}
                  </p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </Bloco>
  );
};

// ─────────────── um KPI ───────────────

const CHIP = {
  sugestao: { rotulo: 'Sugestão', classe: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  proposto: { rotulo: 'Aguardando validação', classe: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  validado: { rotulo: 'Validado', classe: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  recusado: { rotulo: 'Recusado', classe: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' },
} as const;

interface ItemProps {
  slot: Slot;
  ctx: ContextoKpis;
  metasMensais: MetaMensal[];
  nomes: Map<string, string>;
  podeValidar: boolean;
  meuId: string | null;
  alternativas: DefinicaoKpiAuto[];
  onAdotar: (def: DefinicaoKpiAuto, meta: number) => Promise<boolean>;
  onTrocar: (atual: DefinicaoKpiAuto, nova: DefinicaoKpiAuto) => Promise<boolean>;
  onRecusarSugestao: (def: DefinicaoKpiAuto) => Promise<boolean>;
  onAtualizar: (id: number, campos: Partial<KpiDepartamento>, msg: string) => Promise<boolean>;
  onApagar: (id: number) => Promise<boolean>;
}

const ItemKpi: React.FC<ItemProps> = ({ slot, ctx, metasMensais, nomes, podeValidar, meuId, alternativas, onAdotar, onTrocar, onRecusarSugestao, onAtualizar, onApagar }) => {
  const def = slot.def;
  const kpi = slot.tipo === 'db' ? slot.kpi : null;
  const unidade: UnidadeKpi = kpi?.unidade ?? def!.unidade;
  const sentido = kpi?.sentido ?? def!.sentido;

  // Valor: calculado ao vivo quando há cálculo; senão, o digitado pelo gestor.
  const calculado: ValorKpi | null = def ? def.calcular(ctx) : null;
  const valor: ValorKpi | null = def
    ? calculado
    : { atual: kpi!.valor_atual, anterior: null, periodo: kpi!.valor_atualizado_em ? `informado em ${dataCurta(kpi!.valor_atualizado_em)}` : 'sem valor informado', rotuloAnterior: '' };
  const carregando = !!def && calculado == null;
  const metaSugerida = def && calculado ? def.metaSugerida(calculado, ctx) : 0;
  // A meta do KPI (validada ou sugerida) vale nos meses sem meta definida na aba Metas.
  const metaBase = kpi ? kpi.meta : metaSugerida;
  const mesMeta = mesDaMeta(calculado?.periodo, ctx.hoje);
  const doMes = metaDoMes(metasMensais, kpi ? chaveDoKpi(kpi) : def!.id, mesMeta);
  const meta = doMes ? doMes.valor : metaBase;

  // Contagem do mês corrente compara no MESMO ponto do mês: meta e mês anterior
  // proporcionais aos dias corridos (ver `fracao` em kpisAutomaticos.ts).
  const fracao = valor?.fracao ?? 1;
  const parcial = fracao < 0.999;
  const metaAteAgora = meta * fracao;
  const anteriorAteAgora = valor?.anterior == null ? null : valor.anterior * fracao;
  const atingimento = valor ? atingimentoKpi({ meta: metaAteAgora, valor_atual: valor.atual, sentido }) : null;
  const faixa = faixaDoAtingimento(atingimento);
  const melhora = valor ? melhoraDoKpi({ atual: valor.atual, anterior: anteriorAteAgora }, sentido) : null;
  const chip = kpi ? CHIP[kpi.status] : CHIP.sugestao;

  const [aberto, setAberto] = useState(!!kpi && kpi.status === 'proposto' && podeValidar);
  // `null` = ninguém mexeu: o campo mostra a meta atual (que pode chegar depois,
  // quando o número do KPI termina de carregar) em vez de congelar o primeiro valor.
  const [metaEditada, setMetaEditada] = useState<string | null>(null);
  const metaTexto = metaEditada ?? escreverNumero(metaBase);
  const [valorTexto, setValorTexto] = useState(escreverNumero(kpi?.valor_atual));
  const [trocando, setTrocando] = useState(false);
  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const rodar = async (acao: () => Promise<boolean>) => { setOcupado(true); await acao(); setOcupado(false); };
  const metaDigitada = lerNumero(metaTexto) ?? metaBase;
  const souAutor = !!kpi && !!meuId && kpi.proposto_por === meuId;
  const podeApagar = !!kpi && (podeValidar || (souAutor && kpi.status !== 'validado'));

  return (
    <li className={`rounded-lg px-2 py-2.5 transition-colors duration-150 ${aberto ? 'bg-gray-50 dark:bg-gray-900/40' : 'hover:bg-gray-50 dark:hover:bg-gray-900/30'}`}>
      <button type="button" onClick={() => setAberto((a) => !a)} aria-expanded={aberto} className="w-full text-left">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[13px] font-semibold leading-snug text-gray-800 dark:text-gray-100">{kpi?.nome ?? def!.nome}</p>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${chip.classe}`}>{chip.rotulo}</span>
        </div>
        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
          <span className={`font-montserrat text-xl font-bold tabular-nums ${carregando ? 'text-gray-300 dark:text-gray-600' : 'text-gray-900 dark:text-white'}`}>
            {carregando ? '…' : formatarValorKpi(valor?.atual, unidade)}
          </span>
          {melhora != null && Math.abs(melhora) >= 0.005 && (
            <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${melhora > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}
              title={parcial
                ? `${valor!.rotuloAnterior} inteiro: ${formatarValorKpi(valor!.anterior, unidade)}; no mesmo ponto do mês: ${formatarValorKpi(anteriorAteAgora, unidade)}`
                : `${valor!.rotuloAnterior}: ${formatarValorKpi(valor!.anterior, unidade)}`}>
              {(valor!.atual ?? 0) >= (anteriorAteAgora ?? 0) ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
              {pct(Math.abs(melhora))} {melhora > 0 ? 'melhor' : 'pior'} que {valor!.rotuloAnterior}{parcial ? ' no mesmo ponto' : ''}
            </span>
          )}
          <span className="ml-auto text-right text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
            {doMes ? `meta de ${rotuloDoMes(mesMeta).slice(0, 3)} ` : 'meta '}{sentido === 'menor' ? 'até ' : ''}<strong className={`font-semibold ${atingimento == null ? '' : COR_FAIXA[faixa].texto}`}>{formatarValorKpi(meta, unidade)}</strong>
            {parcial && <span className="block text-[10px] text-gray-400">{formatarValorKpi(metaAteAgora, unidade)} até hoje</span>}
          </span>
        </div>
        {atingimento != null && <BarraMeta fracao={atingimento} faixa={faixa} className="mt-2" />}
        <p className="mt-1 flex items-center justify-between text-[10px] text-gray-400">
          {valor?.periodo ?? ''}
          <ChevronDown size={12} className={`transition-transform duration-200 ${aberto ? 'rotate-180' : ''}`} />
        </p>
      </button>

      {aberto && (
        <div className="mt-2 space-y-3 border-t border-gray-200/70 pt-3 text-xs dark:border-gray-700">
          <p className="leading-relaxed text-gray-600 dark:text-gray-300">{kpi?.como_medir ?? def!.como_medir}</p>
          {kpi && (
            <p className="text-gray-400">
              Proposto por {nomes.get(kpi.proposto_por ?? '') ?? 'alguém'} em {dataCurta(kpi.proposto_em)}
              {kpi.validado_em && ` · ${kpi.status === 'validado' ? 'validado' : 'recusado'} por ${nomes.get(kpi.validado_por ?? '') ?? 'alguém'} em ${dataCurta(kpi.validado_em)}`}
            </p>
          )}

          {doMes && (
            <p className="rounded-lg bg-primary/[0.05] px-2.5 py-2 text-gray-600 dark:bg-primary-light/[0.06] dark:text-gray-300">
              Em {rotuloDoMes(mesMeta)} vale a meta de <strong>{formatarValorKpi(doMes.valor, unidade)}</strong>, definida na{' '}
              <Link to="/dashboard/painel-executivo/metas" className="font-semibold text-primary hover:underline">aba Metas</Link>
              {doMes.herdada ? ` em ${rotuloDoMes(doMes.desde)}` : ''}. A meta abaixo é a do KPI e vale nos meses sem meta definida.
            </p>
          )}

          {/* Meta: na sugestão vem preenchida com a sugerida; no KPI salvo, edita a meta dele. */}
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-gray-500">
              Meta
              <input value={metaTexto} onChange={(e) => setMetaEditada(e.target.value)} inputMode="decimal" className={`${CAMPO} w-28 py-1.5 text-xs tabular-nums`} />
            </label>
            {kpi && !def && (
              <label className="flex items-center gap-2 text-gray-500">
                Valor atual
                <input value={valorTexto} onChange={(e) => setValorTexto(e.target.value)} inputMode="decimal" className={`${CAMPO} w-28 py-1.5 text-xs tabular-nums`} />
              </label>
            )}
            {kpi && (
              <BotaoSecundario disabled={ocupado} onClick={() => rodar(() => onAtualizar(kpi.id, {
                meta: metaDigitada,
                ...(def ? {} : { valor_atual: lerNumero(valorTexto) }),
              }, 'KPI atualizado.'))}>
                Salvar
              </BotaoSecundario>
            )}
          </div>

          {recusando ? (
            <div className="space-y-2">
              <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} autoFocus
                placeholder="Por que não vale (opcional): o gestor lê isto" className={`${CAMPO} text-xs`} />
              <div className="flex gap-2">
                <BotaoPrimario className="!bg-rose-600 hover:!bg-rose-700" disabled={ocupado}
                  onClick={() => rodar(async () => {
                    const ok = kpi ? await onAtualizar(kpi.id, { status: 'recusado', comentario_validacao: motivo || null }, 'KPI recusado.') : await onRecusarSugestao(def!);
                    if (ok) setRecusando(false);
                    return ok;
                  })}>
                  Recusar
                </BotaoPrimario>
                <BotaoSecundario onClick={() => setRecusando(false)}>Voltar</BotaoSecundario>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {!kpi && (
                <BotaoPrimario disabled={ocupado || carregando} onClick={() => rodar(() => onAdotar(def!, metaDigitada))}>
                  {ocupado ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                  {podeValidar ? 'Validar' : 'Propor ao Igor'}
                </BotaoPrimario>
              )}
              {kpi && podeValidar && kpi.status === 'proposto' && (
                <BotaoPrimario disabled={ocupado} onClick={() => rodar(() => onAtualizar(kpi.id, { status: 'validado', meta: metaDigitada, comentario_validacao: null }, 'KPI validado.'))}>
                  <Check size={13} /> Validar
                </BotaoPrimario>
              )}
              {!kpi && alternativas.length > 0 && (
                <BotaoSecundario onClick={() => setTrocando((t) => !t)}>
                  <Repeat size={13} /> Trocar
                </BotaoSecundario>
              )}
              {podeValidar && (!kpi || kpi.status === 'proposto') && (
                <BotaoSecundario disabled={ocupado} onClick={() => setRecusando(true)}><X size={13} /> Recusar</BotaoSecundario>
              )}
              {kpi && kpi.status === 'recusado' && (souAutor || podeValidar) && (
                <BotaoSecundario disabled={ocupado} onClick={() => rodar(() => onAtualizar(kpi.id, { status: 'proposto' }, 'KPI reenviado para validação.'))}>
                  <RotateCcw size={13} /> Reenviar
                </BotaoSecundario>
              )}
              {podeApagar && (
                <button type="button" disabled={ocupado}
                  onClick={() => { if (window.confirm(`Apagar o KPI "${kpi!.nome}"? A sugestão volta para a lista.`)) rodar(() => onApagar(kpi!.id)); }}
                  className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium text-gray-400 transition-colors hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-900/20">
                  <Trash2 size={13} /> Apagar
                </button>
              )}
            </div>
          )}

          {trocando && !kpi && (
            <ul className="space-y-1 rounded-lg border border-gray-200 bg-white p-1.5 dark:border-gray-600 dark:bg-gray-800">
              {alternativas.map((alt) => {
                const v = alt.calcular(ctx);
                return (
                  <li key={alt.id}>
                    <button type="button" disabled={ocupado}
                      onClick={() => rodar(async () => { const ok = await onTrocar(def!, alt); if (ok) setTrocando(false); return ok; })}
                      className="flex w-full items-center justify-between gap-3 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/60">
                      <span>
                        <span className="block font-semibold text-gray-800 dark:text-gray-100">{alt.nome}</span>
                        <span className="block text-[10px] text-gray-400">{alt.como_medir}</span>
                      </span>
                      <span className="shrink-0 font-montserrat text-sm font-bold tabular-nums text-gray-900 dark:text-white">
                        {v ? formatarValorKpi(v.atual, alt.unidade) : '…'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </li>
  );
};

// ─────────────── KPI próprio ───────────────

interface FormProps {
  departamentoInicial: string;
  cheio: (dep: string) => boolean;
  onCancelar: () => void;
  onEnviar: (novo: NovoKpi) => Promise<void>;
}

const FormularioKpi: React.FC<FormProps> = ({ departamentoInicial, cheio, onCancelar, onEnviar }) => {
  const [departamento, setDepartamento] = useState(departamentoInicial);
  const [nome, setNome] = useState('');
  const [comoMedir, setComoMedir] = useState('');
  const [unidade, setUnidade] = useState<UnidadeKpi>('numero');
  const [sentido, setSentido] = useState<'maior' | 'menor'>('maior');
  const [meta, setMeta] = useState('');
  const [valor, setValor] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [tentou, setTentou] = useState(false);

  const metaNum = lerNumero(meta);
  const nomeOk = nome.trim().length >= 3 && nome.trim().length <= 80;
  const valido = nomeOk && metaNum != null && !cheio(departamento);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setTentou(true);
    if (!valido) return;
    setEnviando(true);
    await onEnviar({
      departamento, nome: nome.trim(), como_medir: comoMedir.trim() || null, unidade, sentido, meta: metaNum!, valor_atual: lerNumero(valor),
    });
    setEnviando(false);
  };

  return (
    <form onSubmit={enviar} className="mx-5 mb-5 rounded-xl border border-primary/25 bg-primary/[0.03] p-4 dark:border-primary-light/25 dark:bg-primary-light/[0.04]">
      <p className="text-sm font-semibold text-gray-900 dark:text-white">KPI próprio</p>
      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">Para o que o sistema ainda não calcula sozinho. O valor atual é digitado e atualizado pelo gestor.</p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Departamento</span>
          <select value={departamento} onChange={(e) => setDepartamento(e.target.value)} className={CAMPO}>
            {DEPARTAMENTOS.map((d) => (
              <option key={d.id} value={d.id} disabled={cheio(d.id)}>{d.rotulo}{cheio(d.id) ? ' (já tem 3)' : ''}</option>
            ))}
          </select>
        </label>
        <label className="block xl:col-span-3">
          <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Nome do KPI</span>
          <input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={80} placeholder="Ex.: Visitas de campo no mês"
            aria-invalid={tentou && !nomeOk} className={`${CAMPO} ${tentou && !nomeOk ? 'border-rose-400' : ''}`} />
        </label>
        <label className="block md:col-span-2 xl:col-span-4">
          <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Como é medido</span>
          <input value={comoMedir} onChange={(e) => setComoMedir(e.target.value)} placeholder="De onde sai o número e com que frequência é atualizado" className={CAMPO} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Unidade</span>
          <select value={unidade} onChange={(e) => setUnidade(e.target.value as UnidadeKpi)} className={CAMPO}>
            {UNIDADES_KPI.map((u) => <option key={u.id} value={u.id}>{u.rotulo}</option>)}
          </select>
        </label>
        <fieldset className="block">
          <legend className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">O bom é</legend>
          <div className="grid grid-cols-2 rounded-lg border border-gray-200 bg-white p-0.5 dark:border-gray-600 dark:bg-gray-900">
            {(['maior', 'menor'] as const).map((s) => (
              <button key={s} type="button" onClick={() => setSentido(s)} aria-pressed={sentido === s}
                className={`rounded-md px-2 py-1.5 text-xs font-semibold transition-colors duration-150 ${sentido === s ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400'}`}>
                {s === 'maior' ? 'Quanto maior' : 'Quanto menor'}
              </button>
            ))}
          </div>
        </fieldset>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Meta</span>
          <input value={meta} onChange={(e) => setMeta(e.target.value)} inputMode="decimal" placeholder="Ex.: 40"
            aria-invalid={tentou && metaNum == null} className={`${CAMPO} tabular-nums ${tentou && metaNum == null ? 'border-rose-400' : ''}`} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Valor atual <span className="font-normal text-gray-400">(opcional)</span></span>
          <input value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" placeholder="Hoje está em…" className={`${CAMPO} tabular-nums`} />
        </label>
      </div>
      {tentou && !valido && (
        <p className="mt-3 text-xs text-rose-600 dark:text-rose-400">
          {cheio(departamento) ? `${rotuloDepartamento(departamento)} já tem ${LIMITE_KPIS_POR_DEPARTAMENTO} KPIs. Recuse ou apague um antes.` : !nomeOk ? 'Dê um nome de 3 a 80 letras ao KPI.' : 'Informe a meta em número.'}
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <BotaoPrimario type="submit" disabled={enviando} className="!px-4 !py-2 !text-sm">
          {enviando && <Loader2 size={14} className="animate-spin" />} Enviar para validação
        </BotaoPrimario>
        <BotaoSecundario onClick={onCancelar} disabled={enviando} className="!px-4 !py-2 !text-sm">Cancelar</BotaoSecundario>
      </div>
    </form>
  );
};

export default KpisDepartamentos;
